import { Icon, IconName } from "@/components/icons/Icon";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { CountBadgeV2 } from "@/components/interactive/CountBadgeV2";
import {
  IconButtonDropdownMenuItemV2,
  IconButtonDropdownMenuV2,
} from "@/components/interactive/IconButtonDropdownMenuV2";
import { Tooltip, TooltipProps, TooltipV2 } from "@/components/TooltipV2";
import {
  AmplitudeEvent,
  AmplitudeEventPropertiesProvider,
  useAmplitudeOnClick,
} from "@/utils/amplitude";
import { classNames } from "@/utils/classnames";
import { useOverflowItems } from "@/utils/hooks/dom/use-overflow-items";
import { Placement } from "@floating-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

type TabIndicator = { left: number; width: number };
export type TabV2Icon = IconName;
export type TabV2Count = string | number;
export type SliderTabV2Size = "sm" | "md";
export type SliderTabsV2Surface = "default" | "transparent" | "dirty";
export type SliderTabsV2ActiveSurface = "default" | "dirty";
export type TabOverflowMode = "none" | "menu";

// ButtonV2 spaces its label and icon 6px apart; the overflow trigger reads as one
// "n more" plus chevron unit, so its chevron sits 4px from the count.
const TAB_OVERFLOW_TRIGGER_CLASS = "!gap-cds-4";

const getOverflowTriggerText = (count: number) => `${count} more`;

const renderTabIcon = (icon: TabV2Icon | undefined, size: "sm" | "md", className: string) => {
  if (!icon) {
    return null;
  }

  return <Icon name={icon} size={size} aria-hidden={true} className={className} />;
};

const useTabIndicator = <T extends string>(activeItemId: T, items: readonly { id: T }[]) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const [indicator, setIndicator] = useState<TabIndicator | null>(null);

  const syncIndicator = useCallback(() => {
    const activeNode = itemRefs.current[String(activeItemId)];
    if (!activeNode) {
      setIndicator(null);
      return;
    }

    setIndicator({
      left: activeNode.offsetLeft,
      width: activeNode.offsetWidth,
    });
  }, [activeItemId]);

  useEffect(() => {
    syncIndicator();
  }, [syncIndicator, items]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const containerNode = containerRef.current;
    if (!containerNode) {
      return;
    }

    const observer = new ResizeObserver(() => {
      syncIndicator();
    });

    observer.observe(containerNode);
    Object.values(itemRefs.current).forEach((node) => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [items, syncIndicator]);

  return { containerRef, itemRefs, indicator };
};

// Tabs use roving tabIndex, so arrow keys must reach the otherwise unfocusable tabs.
// Moving focus does not activate a panel until Enter or Space is pressed.
const useTabKeyboardNavigation = <T extends string>(
  items: readonly { id: T; disabled?: boolean }[],
  activeItemId: T,
  itemRefs: React.MutableRefObject<Record<string, HTMLElement | null>>,
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>,
) => {
  const handleArrowKeys = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const enabled = items.filter((item) => !item.disabled);
      if (enabled.length === 0) {
        return;
      }

      const focusedIndex = enabled.findIndex(
        (item) => itemRefs.current[String(item.id)] === document.activeElement,
      );
      // Arrowing in from elsewhere starts at the selected tab.
      const from =
        focusedIndex === -1
          ? Math.max(
              0,
              enabled.findIndex((item) => item.id === activeItemId),
            )
          : focusedIndex;

      let nextIndex: number;
      switch (event.key) {
        case "ArrowRight":
          nextIndex = (from + 1) % enabled.length;
          break;
        case "ArrowLeft":
          nextIndex = (from - 1 + enabled.length) % enabled.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = enabled.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      itemRefs.current[String(enabled[nextIndex]!.id)]?.focus();
    },
    [activeItemId, itemRefs, items],
  );

  const handleTabListKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (!event.defaultPrevented) {
        handleArrowKeys(event);
      }
    },
    [handleArrowKeys, onKeyDown],
  );

  return handleTabListKeyDown;
};

export interface SliderTabV2Item<T extends string = string> {
  id: T;
  label: string;
  testId?: string;
  iconOnly?: boolean;
  icon?: TabV2Icon;
  count?: TabV2Count;
  disabled?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipPlacement?: Placement;
}

export interface SliderTabV2Props
  extends Omit<React.ComponentProps<"button">, "children" | "type"> {
  "data-testid"?: string;
  label: string;
  tabId?: string;
  currentSelectionLabel?: string;
  currentSelectionId?: string;
  iconOnly?: boolean;
  icon?: TabV2Icon;
  count?: TabV2Count;
  size?: SliderTabV2Size;
  active?: boolean;
  /**
   * Preview-only override for the hovered visual state.
   *
   * @deprecated Use only in design-system demos/stickersheets. Product surfaces should rely on
   * real hover/focus state.
   */
  forceHovered?: boolean;
  suppressActiveSurface?: boolean;
  flushStart?: boolean;
  flushEnd?: boolean;
  flushTop?: boolean;
}

const SLIDER_TAB_BASE =
  "inline-flex shrink-0 box-border items-center justify-center rounded-cds-4 select-none " +
  "transition-[background-color,color] duration-75";

const SLIDER_TAB_SIZE_CLASSES: Record<SliderTabV2Size, string> = {
  sm: "h-6 text-cds-xs font-medium whitespace-nowrap",
  md: "h-7 text-cds-tab font-cds-tab whitespace-nowrap",
};

// Labelled tabs only. An icon-only tab is a fixed square with no padding, so the same size's
// horizontal padding must not apply to it -- a later `px-*` would beat its `p-0` and push the
// icon off centre.
const SLIDER_TAB_PADDING_CLASSES: Record<SliderTabV2Size, string> = {
  sm: "px-cds-8 [&:has([data-count-badge-v2])]:pr-cds-4",
  md: "px-cds-8 [&:has([data-count-badge-v2])]:pr-cds-4",
};

const SLIDER_TAB_CONTENT_GAP_CLASSES: Record<SliderTabV2Size, string> = {
  sm: "gap-cds-4",
  md: "gap-cds-6",
};

const SLIDER_TAB_ICON_ONLY_SIZE_CLASSES: Record<SliderTabV2Size, string> = {
  sm: "w-6 gap-0",
  md: "w-7 gap-0",
};

const SLIDER_TABS_SIZE_CLASSES: Record<SliderTabV2Size, { container: string; tablist: string }> = {
  sm: { container: "h-7", tablist: "h-6" },
  md: { container: "h-8", tablist: "h-7" },
};

export const SliderTabV2 = React.forwardRef<HTMLButtonElement, SliderTabV2Props>(
  function SliderTabV2Impl(
    {
      label,
      tabId,
      currentSelectionLabel,
      currentSelectionId,
      iconOnly = false,
      icon,
      count,
      size = "md",
      active = false,
      forceHovered = false,
      suppressActiveSurface = false,
      flushStart = false,
      flushEnd = false,
      flushTop = false,
      disabled,
      className,
      onClick,
      "data-testid": dataTestId,
      ...rest
    },
    ref,
  ) {
    const hovered = forceHovered && !active;
    const groupedBaseSurface = suppressActiveSurface && !active && !hovered;
    const amplitudeOnClick = useAmplitudeOnClick({
      event: AmplitudeEvent.DESIGN_SYSTEM_TAB_CLICK,
      component: "SliderTabV2",
      extraProperties: {
        current_selection_label: currentSelectionLabel ?? (active ? label : undefined),
        current_selection_id: currentSelectionId ?? (active ? tabId : undefined),
        new_selection_label: label,
        new_selection_id: tabId,
        data_test_id: dataTestId,
        freeform_selection: false,
      },
    });

    return (
      <button
        ref={ref}
        type={"button"}
        role={"tab"}
        aria-selected={active}
        tabIndex={active ? 0 : -1}
        disabled={disabled}
        data-testid={dataTestId}
        className={classNames(
          SLIDER_TAB_BASE,
          SLIDER_TAB_SIZE_CLASSES[size],
          iconOnly ? SLIDER_TAB_ICON_ONLY_SIZE_CLASSES[size] : SLIDER_TAB_PADDING_CLASSES[size],
          "group relative",
          flushStart && "!rounded-l-none",
          flushEnd && "!rounded-r-none",
          flushTop && "!rounded-t-none",
          active
            ? "text-cds-ink-strong dark:!text-zinc-200"
            : hovered
              ? "text-cds-ink dark:!text-zinc-200"
              : "text-cds-ink-secondary dark:!text-zinc-400",
          !active && !hovered && "hover:text-cds-ink dark:hover:!text-zinc-200",
          active && "cursor-default",
          "focus-visible:outline-none focus-visible:!shadow-cds-focus focus-visible:relative focus-visible:z-10",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
        {...rest}
        onClick={(event) => {
          amplitudeOnClick();
          onClick?.(event);
        }}
      >
        <span
          aria-hidden={true}
          className={classNames(
            "pointer-events-none absolute inset-0 rounded-cds-4 z-0 transition-[background-color,box-shadow] duration-75",
            flushTop && "!rounded-t-none",
            groupedBaseSurface && "bg-cds-surface-active dark:bg-zinc-800",
            active && !suppressActiveSurface && "bg-cds-surface-default dark:bg-white/[0.12]",
            hovered && "bg-cds-surface-hover dark:bg-white/[0.05]",
            !active &&
              !hovered &&
              "group-hover:bg-cds-surface-hover dark:group-hover:bg-white/[0.05]",
          )}
        />
        <span
          className={classNames(
            "relative z-[2] inline-flex min-w-0 items-center",
            SLIDER_TAB_CONTENT_GAP_CLASSES[size],
          )}
        >
          {renderTabIcon(icon, "sm", "shrink-0")}
          <span className={classNames("truncate", iconOnly && "sr-only")}>{label}</span>
        </span>
        {count != null && (
          <CountBadgeV2
            className={"relative z-[2] ml-cds-4"}
            emphasis={active || hovered ? "default" : "unhovered"}
          >
            {String(count)}
          </CountBadgeV2>
        )}
      </button>
    );
  },
);

export interface TabsV2ContentProps extends React.ComponentProps<"div"> {
  /** Controls can be composed separately when a sticky layout renders the rail elsewhere. */
  controls?: React.ReactNode;
  /** Override only for layouts with an explicit content inset. */
  paddingClassName?: string;
}

/** Shared by underline and slider tabs; the opening gap scrolls with the content. */
export const TabsV2Content: React.FC<TabsV2ContentProps> = ({
  controls,
  children,
  className,
  paddingClassName = "pt-cds-12",
  ...props
}) => (
  <>
    {controls}
    <div {...props} data-tab-content="" className={classNames(paddingClassName, className)}>
      {children}
    </div>
  </>
);

interface TabsV2PanelProps {
  children?: React.ReactNode;
  contentProps?: Omit<TabsV2ContentProps, "children" | "controls">;
}

export interface SliderTabsV2Props<T extends string = string>
  extends Omit<React.ComponentProps<"div">, "children" | "onChange">,
    TabsV2PanelProps {
  items: readonly SliderTabV2Item<T>[];
  activeItemId: T;
  ariaLabel?: string;
  /** Size every tab to the intrinsic width of the widest item. */
  equalWidth?: boolean;
  /** Span the container's full width, the tabs sharing it equally. Implies `equalWidth`. */
  fullWidth?: boolean;
  size?: SliderTabV2Size;
  surface?: SliderTabsV2Surface;
  activeSurface?: SliderTabsV2ActiveSurface;
  onChange?: (item: SliderTabV2Item<T>) => void;
  flushStart?: boolean;
  flushEnd?: boolean;
  flushTop?: boolean;
  rightAccessory?: React.ReactNode;
  /** Move tabs that do not fit into a More menu. Defaults to the existing non-overflow layout. */
  overflowMode?: TabOverflowMode;
  /** Accessible label for the overflow trigger and menu. */
  overflowButtonLabel?: string;
}

const BaseSliderTabsV2 = <T extends string = string>({
  items,
  activeItemId,
  ariaLabel,
  equalWidth = false,
  fullWidth = false,
  size = "md",
  surface = "default",
  activeSurface = "default",
  onChange,
  flushStart = false,
  flushEnd = false,
  flushTop = false,
  rightAccessory,
  className,
  onKeyDown,
  ...rest
}: SliderTabsV2Props<T>) => {
  const { containerRef, itemRefs, indicator } = useTabIndicator(activeItemId, items);
  const handleTabListKeyDown = useTabKeyboardNavigation(items, activeItemId, itemRefs, onKeyDown);
  const sizeClasses = SLIDER_TABS_SIZE_CLASSES[size];
  const activeItemIndex = items.findIndex((item) => item.id === activeItemId);

  return (
    <AmplitudeEventPropertiesProvider properties={{ tab: activeItemId }}>
      <div
        data-ds-v2={""}
        data-page-ui=""
        className={classNames(
          "relative isolate overflow-hidden rounded-cds-6 px-cds-2 py-cds-2",
          fullWidth ? "flex w-full" : "inline-flex w-fit",
          flushStart && "!rounded-l-none",
          flushEnd && "!rounded-r-none",
          flushTop && "!rounded-t-none",
          sizeClasses.container,
          "ring-1 ring-inset",
          surface === "transparent" &&
            "bg-transparent ring-transparent dark:bg-transparent dark:ring-transparent",
          surface === "dirty" &&
            "bg-cds-surface-active ring-yellow-300 dark:bg-zinc-800 dark:ring-yellow-400/30",
          surface === "default" &&
            "bg-cds-surface-active ring-cds-surface-active dark:bg-zinc-800 dark:ring-white/[0.04]",
          className,
        )}
        {...rest}
      >
        <div
          ref={containerRef}
          role={"tablist"}
          onKeyDown={handleTabListKeyDown}
          aria-label={ariaLabel}
          className={classNames(
            "relative items-center gap-0",
            fullWidth
              ? "grid w-full grid-flow-col auto-cols-fr"
              : equalWidth
                ? "inline-grid grid-flow-col auto-cols-fr"
                : "inline-flex",
            sizeClasses.tablist,
          )}
        >
          {indicator && (
            <div
              aria-hidden={true}
              className={classNames(
                "pointer-events-none absolute inset-y-0 left-0 z-[1] overflow-hidden rounded-cds-4",
                flushStart && activeItemIndex === 0 && "!rounded-l-none",
                flushEnd && activeItemIndex === items.length - 1 && "!rounded-r-none",
                flushTop && "!rounded-t-none",
                "transition-[transform,width] duration-200 ease-out",
              )}
              style={{
                width: indicator.width,
                transform: `translateX(${indicator.left}px)`,
              }}
            >
              <span
                className={classNames(
                  "absolute inset-0 rounded-cds-4",
                  flushTop && "!rounded-t-none",
                  activeSurface === "dirty"
                    ? "bg-yellow-100/80 dark:bg-yellow-500/20"
                    : "bg-cds-surface-default dark:bg-white/[0.12]",
                )}
              />
            </div>
          )}
          {items.map((item) => {
            const tab = (
              <SliderTabV2
                key={item.id}
                ref={(node) => {
                  itemRefs.current[String(item.id)] = node;
                }}
                label={item.label}
                data-testid={item.testId}
                tabId={String(item.id)}
                currentSelectionLabel={
                  items.find((candidate) => candidate.id === activeItemId)?.label
                }
                currentSelectionId={activeItemId != null ? String(activeItemId) : undefined}
                iconOnly={item.iconOnly}
                icon={item.icon}
                count={item.count}
                size={size}
                active={item.id === activeItemId}
                suppressActiveSurface
                flushStart={flushStart && item.id === items[0]?.id}
                flushEnd={flushEnd && item.id === items[items.length - 1]?.id}
                flushTop={flushTop}
                disabled={item.disabled}
                onClick={() => onChange?.(item)}
              />
            );

            if (!item.tooltipContent) {
              return tab;
            }

            return (
              <TooltipV2
                key={item.id}
                content={item.tooltipContent}
                placement={item.tooltipPlacement ?? "top"}
                persistOnHover={false}
              >
                {/* Equal-width tabs take their width from their cell, so the wrapper passes it on
                    rather than shrinking the tab, and its sliding pill, to the label. */}
                <div className={fullWidth || equalWidth ? "grid" : "inline-flex"}>{tab}</div>
              </TooltipV2>
            );
          })}
        </div>
        {rightAccessory != null && (
          <div className={classNames("flex shrink-0 items-center", sizeClasses.tablist)}>
            {rightAccessory}
          </div>
        )}
      </div>
    </AmplitudeEventPropertiesProvider>
  );
};

const SLIDER_OVERFLOW_RESERVED_WIDTH: Record<SliderTabV2Size, number> = {
  sm: 64,
  md: 68,
};
const SLIDER_TABS_HORIZONTAL_PADDING_PX = 4;

/** Single-row slider tabs that move trailing items into an adjacent overflow menu. */
const OverflowMenuSliderTabsV2 = <T extends string = string>({
  items,
  activeItemId,
  overflowButtonLabel = "More views",
  size = "md",
  className,
  onChange,
  ...props
}: SliderTabsV2Props<T>) => {
  const { containerRef, measurementRefs, overflowItems, visibleItems } = useOverflowItems({
    items,
    activeItemId,
    reservedWidth: SLIDER_OVERFLOW_RESERVED_WIDTH[size],
    baseReservedWidth: SLIDER_TABS_HORIZONTAL_PADDING_PX,
  });
  const overflowMenu =
    overflowItems.length === 0 ? null : (
      <IconButtonDropdownMenuV2
        buttonChrome="button"
        buttonButtonVariant="tertiary"
        buttonClassName={TAB_OVERFLOW_TRIGGER_CLASS}
        buttonIcon="chevronDown"
        buttonIconPlacement="right"
        buttonAriaLabel={overflowButtonLabel}
        buttonLabel={overflowButtonLabel}
        buttonSize={size === "sm" ? "xs" : "sm"}
        buttonText={getOverflowTriggerText(overflowItems.length)}
        buttonTooltip={overflowButtonLabel}
        closeOnMouseLeave={false}
        floatOptions={{ placement: "bottom-end" }}
        width="max-content"
      >
        {overflowItems.map((item) => (
          <IconButtonDropdownMenuItemV2
            key={item.id}
            label={item.label}
            leftIcon={item.icon}
            disabled={item.disabled}
            rightAccessory={
              item.count == null ? undefined : <CountBadgeV2>{String(item.count)}</CountBadgeV2>
            }
            onClick={(event) => {
              if (!item.disabled && !event.defaultPrevented) onChange?.(item);
            }}
          />
        ))}
      </IconButtonDropdownMenuV2>
    );

  return (
    <div
      ref={containerRef}
      data-overflow-slider-tabs-container={""}
      data-page-ui="full"
      className="relative w-full min-w-0 overflow-hidden"
    >
      <BaseSliderTabsV2
        {...props}
        items={visibleItems}
        activeItemId={activeItemId}
        className={classNames("max-w-full", className)}
        onChange={onChange}
        rightAccessory={overflowMenu}
        size={size}
      />
      <div
        aria-hidden={true}
        inert={true}
        className="invisible pointer-events-none absolute left-0 top-0 flex w-max"
      >
        {items.map((item) => (
          <SliderTabV2
            key={item.id}
            ref={(node) => {
              if (node == null) measurementRefs.current.delete(item.id);
              else measurementRefs.current.set(item.id, node);
            }}
            data-overflow-measure-slider-tab={""}
            label={item.label}
            iconOnly={item.iconOnly}
            icon={item.icon}
            count={item.count}
            size={size}
            active={item.id === activeItemId}
            disabled={item.disabled}
          />
        ))}
      </div>
    </div>
  );
};

export const SliderTabsV2 = <T extends string = string>({
  overflowMode = "none",
  overflowButtonLabel,
  children,
  contentProps,
  ...props
}: SliderTabsV2Props<T>) => {
  const controls =
    overflowMode === "menu" ? (
      <OverflowMenuSliderTabsV2 {...props} overflowButtonLabel={overflowButtonLabel} />
    ) : (
      <BaseSliderTabsV2 {...props} />
    );
  return children == null ? (
    controls
  ) : (
    <TabsV2Content {...contentProps} controls={controls}>
      {children}
    </TabsV2Content>
  );
};

export interface UnderlineTabV2Item<T extends string = string> {
  id: T;
  label: string;
  icon?: TabV2Icon;
  count?: TabV2Count;
  disabled?: boolean;
  href?: string;
  shallow?: boolean;
  className?: string;
  tooltipContent?: TooltipProps["content"];
}

export interface UnderlineTabV2Props extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  "data-testid"?: string;
  label: string;
  tabId?: string;
  currentSelectionLabel?: string;
  currentSelectionId?: string;
  icon?: TabV2Icon;
  count?: TabV2Count;
  active?: boolean;
  disabled?: boolean;
  href?: string;
  shallow?: boolean;
  /**
   * Preview-only override for the hovered visual state.
   *
   * @deprecated Use only in design-system demos/stickersheets. Product surfaces should rely on
   * real hover/focus state.
   */
  forceHovered?: boolean;
  suppressActiveUnderline?: boolean;
}

const UNDERLINE_TAB_TEXT = "text-cds-tab font-medium whitespace-nowrap";
const UNDERLINE_TAB_INACTIVE_INK = "text-cds-ink-subtle dark:!text-zinc-500";
const UNDERLINE_TAB_INDICATOR_INSET_PX = 4;

export const UNDERLINE_RAIL_DARK_COLOR_CLASS = "dark:!border-white/[0.08]";

// Avoid the legacy `.dark .border-b` rule, whose added specificity can repaint rails zinc-700.
export const UNDERLINE_TABS_RAIL_CLASS = `border-b-[1px] ${UNDERLINE_RAIL_DARK_COLOR_CLASS}`;

const UNDERLINE_TAB_BASE =
  "inline-flex h-8 shrink-0 box-border items-center justify-center gap-cds-6 " +
  "group relative appearance-none border-b-[1px] bg-transparent px-cds-8 select-none " +
  "no-underline transition-colors duration-75";

/**
 * Dresses the overflow trigger, a tertiary ButtonV2, in inactive underline-tab ink. The variant's
 * own ink rules are `!important` or scoped to `.button:hover`, so the doubled `&` outranks them
 * by specificity, and `text-inherit` keeps the chevron on the label's color in every state.
 */
const UNDERLINE_OVERFLOW_TRIGGER_CLASS = classNames(
  TAB_OVERFLOW_TRIGGER_CLASS,
  UNDERLINE_TAB_INACTIVE_INK,
  "[&&:hover]:text-cds-ink-medium dark:hover:!text-zinc-300",
  "[&&:active]:text-cds-ink-medium dark:active:!text-zinc-300",
  "[&&[aria-expanded=true]]:text-cds-ink-medium dark:aria-expanded:!text-zinc-300",
  "[&&_svg]:!text-inherit [&&:hover_svg]:!text-inherit [&&:active_svg]:!text-inherit",
);

export const UnderlineTabV2 = React.forwardRef<HTMLElement, UnderlineTabV2Props>(
  function UnderlineTabV2Impl(
    {
      label,
      tabId,
      currentSelectionLabel,
      currentSelectionId,
      icon,
      count,
      active = false,
      forceHovered = false,
      suppressActiveUnderline = false,
      disabled,
      href,
      shallow,
      className,
      onClick,
      "data-testid": dataTestId,
      ...rest
    },
    ref,
  ) {
    const hovered = forceHovered && !active && !disabled;
    const activeUnderlineClass = disabled
      ? "bg-cds-ink dark:bg-zinc-800"
      : "bg-cds-ink dark:bg-zinc-300";
    const amplitudeOnClick = useAmplitudeOnClick({
      event: AmplitudeEvent.DESIGN_SYSTEM_TAB_CLICK,
      component: "UnderlineTabV2",
      extraProperties: {
        current_selection_label: currentSelectionLabel ?? (active ? label : undefined),
        current_selection_id: currentSelectionId ?? (active ? tabId : undefined),
        new_selection_label: label,
        new_selection_id: tabId,
        data_test_id: dataTestId,
        freeform_selection: false,
      },
    });

    const content = (
      <>
        <span
          aria-hidden={true}
          data-underline-tab-hover-surface={""}
          className={classNames(
            "pointer-events-none absolute inset-x-0 inset-y-cds-2 z-0 rounded-cds-4",
            "transition-colors duration-75",
            !disabled && !active && hovered && "bg-cds-surface-hover dark:bg-white/[0.05]",
            !disabled &&
              !active &&
              !hovered &&
              "group-hover:bg-cds-surface-hover dark:group-hover:bg-white/[0.05]",
          )}
        />
        {active && !suppressActiveUnderline && (
          <span
            aria-hidden={true}
            data-underline-tab-indicator={""}
            className={classNames(
              "pointer-events-none absolute inset-x-cds-4 -bottom-px z-[2] h-px",
              activeUnderlineClass,
            )}
          />
        )}
        <span className={"relative z-[1] inline-flex min-w-0 items-center gap-cds-6"}>
          {renderTabIcon(icon, "sm", "shrink-0")}
          <span className={"truncate"}>{label}</span>
        </span>
        {count != null && (
          <CountBadgeV2
            className={"relative z-[1]"}
            emphasis={active || hovered ? "default" : "unhovered"}
          >
            {String(count)}
          </CountBadgeV2>
        )}
      </>
    );

    const sharedProps: React.HTMLAttributes<HTMLElement> & { "data-testid"?: string } = {
      ...rest,
      role: "tab",
      "data-testid": dataTestId,
      "aria-selected": active,
      tabIndex: active ? 0 : -1,
      className: classNames(
        UNDERLINE_TAB_BASE,
        UNDERLINE_TAB_TEXT,
        "!border-transparent",
        UNDERLINE_TAB_INACTIVE_INK,
        active && "z-[1] text-cds-ink-strong dark:!text-zinc-200",
        active && "cursor-default",
        hovered && "text-cds-ink-medium dark:!text-zinc-300",
        !active && !disabled && "hover:text-cds-ink-medium dark:hover:!text-zinc-300",
        disabled && "cursor-not-allowed",
        disabled && active && "opacity-50 dark:opacity-100 dark:!text-zinc-700",
        disabled && !active && "opacity-50 dark:opacity-100 dark:!text-zinc-700",
        "focus-visible:outline-none focus-visible:!shadow-cds-focus focus-visible:relative focus-visible:z-10",
        className,
      ),
      onClick: (event) => {
        amplitudeOnClick();
        onClick?.(event);
      },
    };

    if (href && !disabled) {
      return (
        <ChalkLink
          unstyled
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          shallow={shallow}
          {...sharedProps}
        >
          {content}
        </ChalkLink>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={"button"}
        disabled={disabled}
        {...sharedProps}
      >
        {content}
      </button>
    );
  },
);

export interface UnderlineTabsV2Props<T extends string = string>
  extends Omit<React.ComponentProps<"div">, "children" | "onChange">,
    TabsV2PanelProps {
  items: readonly UnderlineTabV2Item<T>[];
  activeItemId: T;
  onChange?: (item: UnderlineTabV2Item<T>) => void;
  fillTabs?: boolean;
  wrap?: boolean;
  rightAccessory?: React.ReactNode;
  rightAccessoryPosition?: "adjacent" | "edge";
  tabListClassName?: string;
  /** Move tabs that do not fit into a More menu. Defaults to the existing non-overflow layout. */
  overflowMode?: TabOverflowMode;
  /** Accessible label for the overflow trigger and menu. */
  overflowButtonLabel?: string;
}

const BaseUnderlineTabsV2 = <T extends string = string>({
  items,
  activeItemId,
  onChange,
  fillTabs = false,
  wrap = false,
  rightAccessory,
  rightAccessoryPosition = "edge",
  tabListClassName,
  className,
  onKeyDown,
  ...rest
}: UnderlineTabsV2Props<T>) => {
  const { containerRef, itemRefs, indicator } = useTabIndicator(activeItemId, items);

  const handleTabListKeyDown = useTabKeyboardNavigation(items, activeItemId, itemRefs, onKeyDown);

  const tabList = (
    <>
      {indicator && !wrap && (
        <span
          aria-hidden={true}
          className={
            "pointer-events-none absolute -bottom-px left-0 z-[2] h-px bg-cds-ink transition-[transform,width] duration-200 ease-out dark:bg-zinc-300"
          }
          style={{
            width: Math.max(0, indicator.width - 2 * UNDERLINE_TAB_INDICATOR_INSET_PX),
            transform: `translateX(${indicator.left + UNDERLINE_TAB_INDICATOR_INSET_PX}px)`,
          }}
        />
      )}
      {items.map((item, index) => {
        let content = (
          <UnderlineTabV2
            key={item.id}
            data-underline-tab-leading={index === 0 ? "" : undefined}
            ref={(node) => {
              itemRefs.current[String(item.id)] = node;
            }}
            label={item.label}
            tabId={String(item.id)}
            currentSelectionLabel={items.find((candidate) => candidate.id === activeItemId)?.label}
            currentSelectionId={activeItemId != null ? String(activeItemId) : undefined}
            icon={item.icon}
            count={item.count}
            active={item.id === activeItemId}
            disabled={item.disabled}
            href={item.href}
            shallow={item.shallow}
            className={classNames(
              "-mb-px",
              fillTabs && "min-w-0 flex-1",
              wrap && "shrink-0",
              item.className,
            )}
            suppressActiveUnderline={!wrap}
            onClick={(event) => {
              if (!item.disabled && !event.defaultPrevented) {
                onChange?.(item);
              }
            }}
          />
        );

        if (item.tooltipContent) {
          content = (
            <Tooltip content={item.tooltipContent} key={item.id}>
              <div>{content}</div>
            </Tooltip>
          );
        }

        return content;
      })}
    </>
  );

  if (rightAccessory != null) {
    return (
      <AmplitudeEventPropertiesProvider properties={{ tab: activeItemId }}>
        <div
          data-ds-v2={""}
          className={classNames(
            "relative inline-flex h-8 w-full shrink-0 items-center !border-cds-surface-border",
            wrap && "!h-auto",
            UNDERLINE_TABS_RAIL_CLASS,
            className,
          )}
          {...rest}
        >
          <div
            ref={containerRef}
            role={"tablist"}
            onKeyDown={handleTabListKeyDown}
            className={classNames(
              "relative inline-flex h-full min-w-0 items-center gap-0",
              wrap && "!h-auto flex-wrap",
              rightAccessoryPosition === "edge" ? "flex-1" : "shrink-0",
              tabListClassName,
            )}
          >
            {tabList}
          </div>
          <div
            data-tab-right-accessory={rightAccessoryPosition}
            className={classNames(
              "flex h-full shrink-0 items-center",
              rightAccessoryPosition === "edge" && "ml-auto",
            )}
          >
            {rightAccessory}
          </div>
        </div>
      </AmplitudeEventPropertiesProvider>
    );
  }

  return (
    <AmplitudeEventPropertiesProvider properties={{ tab: activeItemId }}>
      <div
        ref={containerRef}
        data-ds-v2={""}
        role={"tablist"}
        className={classNames(
          "relative inline-flex h-8 w-full shrink-0 items-center gap-0 !border-cds-surface-border",
          wrap && "!h-auto flex-wrap",
          UNDERLINE_TABS_RAIL_CLASS,
          tabListClassName,
          className,
        )}
        {...rest}
        onKeyDown={handleTabListKeyDown}
      >
        {tabList}
      </div>
    </AmplitudeEventPropertiesProvider>
  );
};

/**
 * Underline tabs that move trailing items into a menu when the rail becomes too narrow.
 * The active tab is promoted into the visible rail so the selected context never disappears.
 */
const OverflowMenuUnderlineTabsV2 = <T extends string = string>({
  items,
  activeItemId,
  overflowButtonLabel = "More tabs",
  rightAccessory,
  rightAccessoryPosition = "edge",
  ...props
}: UnderlineTabsV2Props<T>) => {
  const { containerRef, measurementRefs, overflowItems, visibleItems } = useOverflowItems({
    items,
    activeItemId,
    reservedWidth: 68,
  });
  const overflowMenu =
    overflowItems.length === 0 ? null : (
      <IconButtonDropdownMenuV2
        buttonChrome="button"
        buttonButtonVariant="tertiary"
        buttonClassName={UNDERLINE_OVERFLOW_TRIGGER_CLASS}
        buttonIcon="chevronDown"
        buttonIconPlacement="right"
        buttonAriaLabel={overflowButtonLabel}
        buttonLabel={overflowButtonLabel}
        buttonSize="sm"
        buttonText={getOverflowTriggerText(overflowItems.length)}
        buttonTooltip={overflowButtonLabel}
        closeOnMouseLeave={false}
        floatOptions={{ placement: "bottom-end" }}
        width="max-content"
      >
        {overflowItems.map((item) => (
          // A collapsed tab keeps its tooltip; TooltipV2 renders the row untouched when empty.
          <TooltipV2 key={item.id} content={item.tooltipContent} placement={"right"}>
            <IconButtonDropdownMenuItemV2
              label={item.label}
              leftIcon={item.icon}
              disabled={item.disabled}
              href={item.disabled ? undefined : item.href}
              shallow={item.shallow}
              rightAccessory={
                item.count == null ? undefined : <CountBadgeV2>{String(item.count)}</CountBadgeV2>
              }
              onClick={(event) => {
                if (!item.disabled && !event.defaultPrevented) props.onChange?.(item);
              }}
            />
          </TooltipV2>
        ))}
      </IconButtonDropdownMenuV2>
    );

  return (
    <div className="flex w-full min-w-0 items-center">
      <div
        ref={containerRef}
        data-overflow-tabs-container={""}
        className={classNames(
          "relative min-w-0 overflow-x-clip overflow-y-visible",
          rightAccessoryPosition === "edge" ? "flex-1" : "max-w-full flex-initial",
        )}
      >
        <BaseUnderlineTabsV2
          {...props}
          items={visibleItems}
          activeItemId={activeItemId}
          rightAccessory={overflowMenu}
          rightAccessoryPosition="adjacent"
        />
        <div
          aria-hidden={true}
          inert={true}
          className="invisible pointer-events-none absolute left-0 top-0 flex w-max"
        >
          {items.map((item, index) => (
            <UnderlineTabV2
              key={item.id}
              data-underline-tab-leading={index === 0 ? "" : undefined}
              ref={(node) => {
                if (node == null) measurementRefs.current.delete(item.id);
                else measurementRefs.current.set(item.id, node);
              }}
              data-overflow-measure-tab={""}
              label={item.label}
              icon={item.icon}
              count={item.count}
              active={item.id === activeItemId}
              disabled={item.disabled}
              className={item.className}
            />
          ))}
        </div>
      </div>
      {rightAccessory != null && (
        <div
          data-tab-right-accessory={rightAccessoryPosition}
          className="flex h-8 shrink-0 items-center"
        >
          {rightAccessory}
        </div>
      )}
    </div>
  );
};

export const UnderlineTabsV2 = <T extends string = string>({
  overflowMode = "none",
  overflowButtonLabel,
  children,
  contentProps,
  ...props
}: UnderlineTabsV2Props<T>) => {
  const controls =
    overflowMode === "menu" && !props.wrap && !props.fillTabs ? (
      <OverflowMenuUnderlineTabsV2 {...props} overflowButtonLabel={overflowButtonLabel} />
    ) : (
      <BaseUnderlineTabsV2 {...props} />
    );
  return children == null ? (
    controls
  ) : (
    <TabsV2Content {...contentProps} controls={controls}>
      {children}
    </TabsV2Content>
  );
};

export interface PanelUnderlineTabsV2Props<T extends string = string>
  extends UnderlineTabsV2Props<T> {
  /** Fixed trailing control; the tab rail scrolls within the remaining width. */
  trailingControl?: React.ReactNode;
}

/**
 * Underline tabs positioned for bordered panels and bottom drawers.
 * The overflow wrapper keeps the active underline aligned with the panel seam.
 */
export const PanelUnderlineTabsV2 = <T extends string = string>({
  trailingControl,
  className,
  children,
  contentProps,
  ...props
}: PanelUnderlineTabsV2Props<T>) => {
  const controls = (
    <div
      className={classNames(
        "relative z-10 flex min-w-0 shrink-0 items-start pt-px",
        children == null && "mb-2",
      )}
    >
      <div className="-mb-cds-2 min-w-0 flex-1 overflow-x-auto pb-cds-2">
        <UnderlineTabsV2
          {...props}
          overflowMode="none"
          className={classNames(
            "min-w-max px-cds-6 dark:!border-white/[0.06] [&_[role=tab]]:!h-7",
            className,
          )}
        />
      </div>
      {trailingControl != null && (
        <div className="flex h-8 shrink-0 items-center border-b border-cds-surface-border px-cds-2 dark:!border-white/[0.06]">
          {trailingControl}
        </div>
      )}
    </div>
  );
  return children == null ? (
    controls
  ) : (
    <TabsV2Content {...contentProps} controls={controls}>
      {children}
    </TabsV2Content>
  );
};
